import { getWebApp } from '@tg-web/utils';

import { DEFAULT_STALE_TIME, useShowUserProfile } from '../api';
import { parseStartappParam } from './parseStartappParam';

export const AllowedLangs = ['ru', 'en', 'fr', 'pt-BR', 'de', 'zh-CN'] as const;
export type AllowedLang = (typeof AllowedLangs)[number];

/**
 * Exact match, so casing and region subtags matter: 'pt-br' fails, 'pt-BR' passes.
 *
 * That is safe only because tgcal_backend normalises first —
 * `Users::AuthService#detect_language_code` maps Telegram's lowercase 'pt-br' to
 * 'pt-BR' before the profile is ever returned to us. Adding a locale whose
 * Telegram tag differs in case or carries a region subtag means editing that
 * method too; the swagger contract will not catch it, because `language_code` is
 * typed as a plain string.
 */
export function isAllowedLang(lang: string): lang is AllowedLang {
  return (AllowedLangs as readonly string[]).includes(lang);
}

export function useAppLang() {
  const userProfileReq = useShowUserProfile(
    {},
    { staleTime: DEFAULT_STALE_TIME },
  );

  let userLangFromStartParam: AllowedLang | null = null;
  try {
    const startParam = getWebApp().initDataUnsafe.start_param;
    if (startParam) {
      userLangFromStartParam =
        (parseStartappParam(startParam).lang as AllowedLang) ?? null;
    }
  } catch (_) {
    // just ignore start params
  }
  const userLangFromTG = getWebApp().initDataUnsafe.user?.language_code;
  const userLang =
    userLangFromStartParam ??
    userProfileReq.data?.user.language_code ??
    userLangFromTG;
  const appLang: AllowedLang =
    userLang && isAllowedLang(userLang) ? userLang : 'en';

  return {
    appLang,
    isReady: userProfileReq.isSuccess,
  };
}
